import React from 'react';
import { Button, DatePicker, InputNumber, Radio, Select, Space, Typography } from 'antd';
import moment, { Moment } from 'moment';
import dayjs from 'dayjs';
import { useIntl } from 'umi';

import Drawer from '@/components/Drawer';
import {
  RECURRENCE_MAX_INTERVAL,
  RECURRENCE_MAX_OCCURRENCES,
  RecurrenceConfig,
  RecurrenceEndType,
  RecurrenceFrequency,
  RecurrenceMonthlyMode,
  Weekday,
} from '@/typings/models/Recurrence';
import {
  defaultRecurrenceConfig,
  describeRecurrence,
  nthWeekdayOfMonth,
  weekdayLabel,
  weekdayForDate,
  weekdayShortLabel,
} from '@/utils/recurrence';
import styles from './recurrence.less';

/** Monday-first ordering for the weekday toggles. */
const WEEKLY_ORDER: Weekday[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const MAX_DAY_OF_MONTH = 31;

interface CustomRecurrenceDrawerProps {
  open: boolean;
  /** Current recurrence config to seed the editor, or null for a fresh rule. */
  value?: RecurrenceConfig | null;
  /** The event start — anchors monthly phrasing and weekly defaults. */
  start?: Moment | null;
  onOk: (config: RecurrenceConfig) => void;
  onCancel: () => void;
}

/**
 * Detailed recurrence editor, modeled on Google Calendar's custom dialog. Opens as a
 * drawer stacked on top of the calendar-event drawer (see `@/components/Drawer`,
 * which stacks nested instances by z-index) rather than a modal, so it doesn't
 * obscure the whole form behind it.
 */
const CustomRecurrenceDrawer: React.FC<CustomRecurrenceDrawerProps> = ({ open, value, start, onOk, onCancel }) => {
  const intl = useIntl();
  const anchor = moment.isMoment(start) ? start : moment();
  const [config, setConfig] = React.useState<RecurrenceConfig>(() => value || defaultRecurrenceConfig(anchor));

  React.useEffect(() => {
    if (open) setConfig(value || defaultRecurrenceConfig(anchor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const update = (patch: Partial<RecurrenceConfig>) => setConfig((c) => ({ ...c, ...patch }));
  const updateEnd = (patch: Partial<RecurrenceConfig['end']>) =>
    setConfig((c) => ({ ...c, end: { ...c.end, ...patch } }));

  const toggleWeekday = (weekday: Weekday) => {
    const current = config.byWeekdays || [weekdayForDate(anchor)];
    const next = current.includes(weekday) ? current.filter((d) => d !== weekday) : [...current, weekday];
    update({ byWeekdays: next.length ? next : current });
  };

  const interval = config.interval || 1;
  const unitOptions = [
    {
      value: RecurrenceFrequency.DAILY,
      label: intl.formatMessage({ id: 'pages.calendar.recurrence.unit.day', defaultMessage: '{n, plural, one {day} other {days}}' }, { n: interval }),
    },
    {
      value: RecurrenceFrequency.WEEKLY,
      label: intl.formatMessage({ id: 'pages.calendar.recurrence.unit.week', defaultMessage: '{n, plural, one {week} other {weeks}}' }, { n: interval }),
    },
    {
      value: RecurrenceFrequency.MONTHLY,
      label: intl.formatMessage({ id: 'pages.calendar.recurrence.unit.month', defaultMessage: '{n, plural, one {month} other {months}}' }, { n: interval }),
    },
  ];

  const monthlyByWeekdayLabel = intl.formatMessage(
    { id: 'pages.calendar.recurrence.monthlyOnThe', defaultMessage: 'Monthly on the {ordinal} {weekday}' },
    {
      ordinal: intl.formatMessage({ id: `common.ordinal.${nthWeekdayOfMonth(anchor)}`, defaultMessage: `${nthWeekdayOfMonth(anchor)}` }),
      weekday: weekdayLabel(weekdayForDate(anchor), intl.locale),
    },
  );

  const preview = describeRecurrence(config, anchor, intl);

  const handleOk = () => {
    const normalized = { ...config };
    // Keep dayOfMonth only when it deviates from the anchor date, so an
    // untouched rule still matches the "Monthly on day N" preset and keeps
    // following the event's start date.
    if (normalized.dayOfMonth === anchor.date()) delete normalized.dayOfMonth;
    onOk(normalized);
  };

  return (
    <Drawer
      open={open}
      width={480}
      title={intl.formatMessage({ id: 'pages.calendar.recurrence.customTitle', defaultMessage: 'Custom recurrence' })}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button size={'large'} onClick={onCancel}>
              {intl.formatMessage({ id: 'common.actions.cancel', defaultMessage: 'Cancel' })}
            </Button>
            <Button
              size={'large'}
              type={'primary'}
              onClick={handleOk}
              data-testid="recurrence-custom-confirm"
            >
              {intl.formatMessage({ id: 'common.actions.done', defaultMessage: 'Done' })}
            </Button>
          </Space>
        </div>
      }
    >
      <div className={styles.row}>
        <span className={styles.label}>
          {intl.formatMessage({ id: 'pages.calendar.recurrence.repeatEvery', defaultMessage: 'Repeat every' })}
        </span>
        <Space.Compact>
          <InputNumber
            min={1}
            max={RECURRENCE_MAX_INTERVAL}
            value={interval}
            onChange={(n) => update({ interval: Number(n) || 1 })}
            data-testid="recurrence-interval"
          />
          <Select
            style={{ minWidth: 120 }}
            value={config.frequency}
            options={unitOptions}
            onChange={(frequency) => update({ frequency })}
            data-testid="recurrence-frequency"
          />
        </Space.Compact>
      </div>

      {config.frequency === RecurrenceFrequency.WEEKLY && (
        <div className={styles.row}>
          <span className={styles.label}>
            {intl.formatMessage({ id: 'pages.calendar.recurrence.repeatOn', defaultMessage: 'Repeat on' })}
          </span>
          <div className={styles.weekdays}>
            {WEEKLY_ORDER.map((weekday) => {
              const active = (config.byWeekdays || [weekdayForDate(anchor)]).includes(weekday);
              return (
                <Button
                  key={weekday}
                  shape="circle"
                  type={active ? 'primary' : 'default'}
                  onClick={() => toggleWeekday(weekday)}
                  data-testid={`recurrence-weekday-${weekday}`}
                >
                  {weekdayShortLabel(weekday, intl.locale)}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {config.frequency === RecurrenceFrequency.MONTHLY && (
        <div className={styles.row}>
          <Radio.Group
            value={config.monthlyMode || RecurrenceMonthlyMode.DAY_OF_MONTH}
            onChange={(e) => update({ monthlyMode: e.target.value })}
          >
            <Space direction="vertical">
              <Radio value={RecurrenceMonthlyMode.DAY_OF_MONTH}>
                <div className={styles.endRow}>
                  {intl.formatMessage({ id: 'pages.calendar.recurrence.monthlyOnDayLabel', defaultMessage: 'Monthly on day' })}
                  <InputNumber
                    min={1}
                    max={MAX_DAY_OF_MONTH}
                    disabled={(config.monthlyMode || RecurrenceMonthlyMode.DAY_OF_MONTH) !== RecurrenceMonthlyMode.DAY_OF_MONTH}
                    value={config.dayOfMonth ?? anchor.date()}
                    onChange={(n) => update({ dayOfMonth: Number(n) || anchor.date() })}
                    data-testid="recurrence-monthly-day"
                  />
                </div>
              </Radio>
              <Radio value={RecurrenceMonthlyMode.WEEKDAY}>{monthlyByWeekdayLabel}</Radio>
            </Space>
          </Radio.Group>
        </div>
      )}

      <div className={styles.row}>
        <span className={styles.label}>
          {intl.formatMessage({ id: 'pages.calendar.recurrence.ends', defaultMessage: 'Ends' })}
        </span>
        <Radio.Group
          value={config.end.type}
          onChange={(e) => updateEnd({ type: e.target.value })}
        >
          <Space direction="vertical">
            <Radio value={RecurrenceEndType.NEVER}>
              {intl.formatMessage({ id: 'pages.calendar.recurrence.endNever', defaultMessage: 'Never' })}
            </Radio>
            <Radio value={RecurrenceEndType.ON_DATE}>
              <div className={styles.endRow}>
                {intl.formatMessage({ id: 'pages.calendar.recurrence.endOn', defaultMessage: 'On' })}
                <DatePicker
                  disabled={config.end.type !== RecurrenceEndType.ON_DATE}
                  value={config.end.until ? dayjs(config.end.until) : undefined}
                  onChange={(d) => updateEnd({ until: d ? d.toISOString() : undefined })}
                  data-testid="recurrence-end-date"
                />
              </div>
            </Radio>
            <Radio value={RecurrenceEndType.AFTER_OCCURRENCES}>
              <div className={styles.endRow}>
                {intl.formatMessage({ id: 'pages.calendar.recurrence.endAfter', defaultMessage: 'After' })}
                <InputNumber
                  min={1}
                  max={RECURRENCE_MAX_OCCURRENCES}
                  disabled={config.end.type !== RecurrenceEndType.AFTER_OCCURRENCES}
                  value={config.end.count || 1}
                  onChange={(n) => updateEnd({ count: Number(n) || 1 })}
                  data-testid="recurrence-end-count"
                />
                {intl.formatMessage({ id: 'pages.calendar.recurrence.occurrences', defaultMessage: 'occurrences' })}
              </div>
            </Radio>
          </Space>
        </Radio.Group>
      </div>

      <Typography.Text type="secondary" className={styles.preview} data-testid="recurrence-preview">
        {preview}
      </Typography.Text>
    </Drawer>
  );
};

export default CustomRecurrenceDrawer;
